import * as React from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import ChangedResponses from 'bundles/compound-assessments/components/local-state/changed-response/ChangedResponses';
import type { ResponseWithId } from 'bundles/compound-assessments/components/local-state/changed-response/ResponseWithId';
import type { ChangedResponse } from 'bundles/compound-assessments/components/local-state/changed-response/types';
import type { MutationInjectedProps } from 'bundles/grading/api/GradingMutationWrapper';
import type { AssignmentRubric, QuestionRubric } from 'bundles/grading/api/types';
import { SGAPageContext } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';
import { useAiSuggestionsContext } from 'bundles/grading/components/staff-graded-assignments/ai-suggestions/AiSuggestionsContext';
import { ChangedResponsesAggregator } from 'bundles/grading/components/staff-graded-assignments/auto-save/ChangedResponsesAggregator';
import { useAutoSaveTasksManager } from 'bundles/grading/components/staff-graded-assignments/auto-save/useAutoSaveTasksManager';
import type {
  AutoSaveContextType,
  ReviewPartProgress,
} from 'bundles/grading/components/staff-graded-assignments/auto-save/utils';
import {
  AutoSaveContext,
  constructDefaultChangedResponse,
  defaultReviewPartProgress,
  getReviewPartProgress,
} from 'bundles/grading/components/staff-graded-assignments/auto-save/utils';
import NavigationPrompt from 'bundles/grading/components/staff-graded-assignments/notifications/NavigationPrompt';

import _t from 'i18n!nls/grading';

type Props = {
  mutations: MutationInjectedProps;
  children: React.ReactNode;
};

const runGetReviewPartProgress = (
  defaultChangedResponses: ResponseWithId[],
  rubrics?: (AssignmentRubric | QuestionRubric)[],
  changedResponses?: ChangedResponse[]
): ReviewPartProgress => {
  if (rubrics) {
    return getReviewPartProgress(rubrics, changedResponses?.length ? changedResponses : defaultChangedResponses);
  } else {
    const res = { ...defaultReviewPartProgress() };
    return res;
  }
};

export const AutoSaveProvider = ({ mutations, children }: Props) => {
  const { pageData, submissionData } = useContext(SGAPageContext);
  const { submission, gradingStepActionMetadata, submitter } = submissionData.data || {};

  // currentSubmitterId and submission have one source of truth: query response.
  // don't use submitterId from URL as submissionData for that is loaded with delay.
  const currentSubmitterId = submitter?.submitterId || '';

  const { tracker, hasUnsavedChanges, isSaving, isSubmitting, register, setData, getData, save } =
    useAutoSaveTasksManager(mutations.batchRubricUpdate, mutations.submitGrades);

  // ReviewPartProgress to be displayed on a page for current submission
  const [reviewPartProgress, setReviewPartProgress] = useState(defaultReviewPartProgress());

  const {
    currentAiReviewPartSuggestion,
    currentAiReviewPartSuggestionApplied,
    currentAiReviewPartSuggestionEdited,
    trackSuggestionEdited,
  } = useAiSuggestionsContext();

  // FormPart component uses `ChangedResponse` to store rubric value in local ApolloCache. By default,
  // ApolloCache receives rubric value only after it is rendered within `ChangedResponse`. Here we provide
  // `ChangedResponses` component with default values for all rubrics even if they have not been rendered yet.
  // This way, we get values of all rubrics on any single rubric update and can send this data to Mutation.
  const defaultChangedResponses = useMemo<ResponseWithId[]>(
    () =>
      Object.keys(submission?.rubricMap || {}).reduce((result, rubricId) => {
        const rubric = submission?.getRubric(rubricId);
        return rubric ? [...result, constructDefaultChangedResponse(rubric)] : result;
      }, [] as ResponseWithId[]),
    [submission]
  );

  // Register a new instance of `AutoSaveTask` on submission load if it does not exist yet.
  useEffect(() => {
    register(currentSubmitterId, pageData, gradingStepActionMetadata || null);
  }, [register, currentSubmitterId, pageData, gradingStepActionMetadata]);

  // set `ReviewPartProgress` on `submission` load.
  useEffect(() => {
    const newReviewPartProgress = runGetReviewPartProgress(
      defaultChangedResponses,
      submission?.getAllRubrics(),
      getData(currentSubmitterId)
    );
    setReviewPartProgress(newReviewPartProgress);
  }, [getData, submission, currentSubmitterId, defaultChangedResponses]);

  // localScopeId is used to scope rubric responses properly in Apollo cache
  const localScopeId =
    gradingStepActionMetadata && currentSubmitterId
      ? `${gradingStepActionMetadata?.gradingTaskIdHash}~~${currentSubmitterId}`
      : '';

  // Handles `ChangedResponse` change and triggers debouncedSave in related AutoSaveTask.
  // `value=null` flags to skip `BatchUpdate` and proceed to `SubmitToGradebook` (if all
  // rubrics are complete)
  const handleChangedResponses = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (value: ChangedResponse[] | null) => {
      if (value) {
        setData(currentSubmitterId, value);
      }

      // check if this changed response is changing a preexisting ai suggestion that was
      // already applied, if so then track the initial edit
      if (
        currentAiReviewPartSuggestion &&
        currentAiReviewPartSuggestionApplied &&
        !currentAiReviewPartSuggestionEdited
      ) {
        trackSuggestionEdited();
      }

      // reset ReviewPartProgress if any changes
      const updatedReviewPartProgress = runGetReviewPartProgress(
        defaultChangedResponses,
        submission?.getAllRubrics(),
        getData(currentSubmitterId)
      );
      setReviewPartProgress(updatedReviewPartProgress);

      // call `debouncedSave`
      save(currentSubmitterId, updatedReviewPartProgress, !value);
    }, // we do not want currentAiReviewPartSuggestionApplied and currentAiReviewPartSuggestionEdited within useCallback
    // dependencies because it will cause any type of changedResponse to behave as an edit with potential stale states
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentSubmitterId, submission, defaultChangedResponses, getData, setData, save]
  );

  // AutoSave context to be accessible by any nested component
  const contextValue: AutoSaveContextType = {
    hasUnsavedChanges,
    isSaving,
    isSubmitting,
    autoSaveTaskStates: tracker,
    localScopeId,
    reviewPartProgress,
    forceSave: () => !isSaving && handleChangedResponses(null),
  };

  const responseIds = defaultChangedResponses.map((item) => item.id);
  return (
    <AutoSaveContext.Provider value={contextValue}>
      {localScopeId && (
        <ChangedResponses ids={responseIds} localScopeId={localScopeId} responses={defaultChangedResponses}>
          {(responses) => {
            return (
              <ChangedResponsesAggregator
                localScopeId={localScopeId}
                responses={responses || []}
                setLatest={handleChangedResponses}
              />
            );
          }}
        </ChangedResponses>
      )}
      <NavigationPrompt
        ignorePathSegment="quick-grader"
        when={hasUnsavedChanges || isSaving || isSubmitting}
        message={_t('Leave the page? Changes you made might not be saved.')}
      />
      {children}
    </AutoSaveContext.Provider>
  );
};
